import { Duration } from 'dayjs/plugin/duration.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { FlexibleServiceDuration } from './flexible-service-duration.js';
import { Service } from './service.js';
import { TimeOfDay } from './time-of-day.js';

export class CalendarOrderItem extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '41ecebd379a54e98a8e8437a4ec98be0';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly duration: Duration;
  public readonly serviceId: string;

  constructor(
    public readonly timeOfDay: TimeOfDay,
    public readonly service: Service,
    duration: Duration | null = null,
  ) {
    super();
    this.serviceId = service.uniqueId;
    this.duration = duration ?? service.getDuration();
  }

  get $typeId(): string {
    return CalendarOrderItem.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CalendarOrderItem.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CalendarOrderItem {
    return new CalendarOrderItem(
        context.fromData<TimeOfDay>(MapUtils.getRaw(map, 'timeOfDay') as SerializedData),
        context.fromData<Service>(MapUtils.getRaw(map, 'service') as SerializedData),
        context.decodeDuration(MapUtils.get<number>(map, 'duration')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'timeOfDay': context.toData(this.timeOfDay),
      'service': context.toData(this.service),
      'duration': context.encodeDuration(this.duration),
    };
  }

  clone(): this {
    return new CalendarOrderItem(
        this.timeOfDay.clone(),
        this.service.clone(),
        this.duration.clone(),
    ) as this;
  }

  isSame(other: CalendarOrderItem): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.timeOfDay, this.timeOfDay) &&
        isSameData(other.service, this.service) &&
        other.duration === this.duration &&
        other.serviceId === this.serviceId;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new CalendarOrderItem(
        this.timeOfDay.clone(),
        this.service.copyWithBusinessRef(businessRef),
        this.duration.clone(),
    ) as this;
  }
}

export class CalendarOrderItemBuilder extends DataModelBuilder<CalendarOrderItem> {
  build(context: SerializationContext, map: SerializedData): CalendarOrderItem {
    return CalendarOrderItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CalendarOrderItem.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CalendarOrderItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      const flexibleDuration = context.fromData<FlexibleServiceDuration>(
        MapUtils.getRaw(map, 'flexibleDuration') as SerializedData,
      );
      MapUtils.set(map, 'duration', context.encodeDuration(flexibleDuration.totalDuration));

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
